/**
 * Profile → My businesses: the order of the business switcher. It belongs to the person
 * (`users.workspaceOrder`), not to a business, so everyone sharing a business keeps their own.
 * Arrows rather than dragging: they work with the keyboard and on the phone.
 */
import { ArrowDown, ArrowUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSaveWorkspaceOrder } from "@/hooks/use-workspaces";
import { useAuth } from "@/lib/session/auth-context";
import { useWorkspace } from "@/lib/session/workspace-context";

export function WorkspaceOrderSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { workspaces } = useWorkspace();
  const save = useSaveWorkspaceOrder();
  if (!user || workspaces.length < 2) return null;

  async function move(index: number, by: -1 | 1) {
    const ids = workspaces.map((workspace) => workspace.id);
    [ids[index], ids[index + by]] = [ids[index + by]!, ids[index]!];
    try {
      await save.mutateAsync({ userId: user!.id, ids });
    } catch {
      toast.error(t("profile.workspaceOrder.error"));
    }
  }

  return (
    <Card data-testid="workspace-order">
      <CardHeader>
        <CardTitle>{t("profile.workspaceOrder.title")}</CardTitle>
        <CardDescription>{t("profile.workspaceOrder.hint")}</CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="divide-y rounded-md border bg-card">
          {workspaces.map((workspace, index) => (
            <li
              key={workspace.id}
              className="flex items-center gap-1 py-1 pr-1 pl-3"
              data-testid="workspace-order-item"
            >
              <span className="min-w-0 flex-1 truncate text-sm">{workspace.name}</span>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("profile.workspaceOrder.up", { name: workspace.name })}
                disabled={index === 0 || save.isPending}
                onClick={() => void move(index, -1)}
              >
                <ArrowUp />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("profile.workspaceOrder.down", { name: workspace.name })}
                disabled={index === workspaces.length - 1 || save.isPending}
                onClick={() => void move(index, 1)}
              >
                <ArrowDown />
              </Button>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
